/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FiUsers,
    FiAward,
    FiTrendingUp,
    FiGlobe,
    FiBriefcase,
    FiHeart,
    FiZap,
    FiTarget,
    FiCheck,
    FiBarChart2,
    FiHome,
    FiMap,
    FiArrowRight,
    FiPlay,
    FiChevronDown
} from 'react-icons/fi';
import { Helmet } from 'react-helmet';
import SecretInvestorsSection from './SecretInvestorsSection';

// Real company data
const companyData = {
    founded: "2022",
    teamSize: "15+",
    clients: "50+",
    satisfaction: "95%",
    mission: "To revolutionize digital experiences through innovative solutions that drive business growth and customer satisfaction.",
    vision: "To be the most trusted digital partner for businesses worldwide by 2030."
};

// Memoized components
const StatCard = ({ stat }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-xl transition-all border border-gray-100 group relative overflow-hidden"
    >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        <div className="relative z-10">
            <div className="text-blue-600 text-2xl mb-3 flex justify-center">
                {stat.icon}
            </div>
            <p className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
            <p className="text-gray-600">{stat.label}</p>
        </div>
    </motion.div>
);



const AboutUsPage = () => {
    const [activeSection, setActiveSection] = useState('story');
    const [isMobile, setIsMobile] = useState(false);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    const timeline = useMemo(() => [
        {
            year: "2022",
            event: "Company Founded",
            description: "Launched our operations with 5 passionate team members working remotely from India",
            icon: <FiBriefcase className="text-white" />
        },
        {
            year: "2022 Q3",
            event: "First Clients",
            description: "Onboarded our first 10 paying clients within 3 months of operation",
            icon: <FiAward className="text-white" />
        },
        {
            year: "2023",
            event: "Team Expansion",
            description: "Expanded to 15 full-time employees and opened our first physical office to support our growing team.",
            icon: <FiUsers className="text-white" />
        },
        {
            year: "2024 Q1",
            event: "Client Milestone",
            description: "Achieved 30+ active clients across various industries",
            icon: <FiBarChart2 className="text-white" />
        },
        {
            year: "2024 Q3",
            event: "First International Client",
            description: "Secured our first client in the United States, marking global expansion",
            icon: <FiGlobe className="text-white" />
        },
        {
            year: "2025",
            event: "Global Expansion",
            description: "Established operations in 3+ countries with 50+ international clients",
            icon: <FiMap className="text-white" />
        },
        {
            year: "2026",
            event: "Physical Presence",
            description: "Opened regional offices in USA, Australia, and Europe to serve clients locally",
            icon: <FiHome className="text-white" />
        }
    ], []);

    const values = useMemo(() => [
        {
            title: "Innovation",
            description: "We challenge the status quo and push boundaries to create breakthrough solutions.",
            icon: <FiZap className="text-white text-2xl" />,
            color: "from-blue-500 to-blue-600"
        },
        {
            title: "Integrity",
            description: "We do what's right, not what's easy. Honesty and transparency guide every decision.",
            icon: <FiHeart className="text-white text-2xl" />,
            color: "from-purple-500 to-purple-600"
        },
        {
            title: "Impact",
            description: "We measure success by the positive change we create for our clients and community.",
            icon: <FiAward className="text-white text-2xl" />,
            color: "from-green-500 to-green-600"
        },
        {
            title: "Excellence",
            description: "We strive for the highest quality in everything we do, from code to customer service.",
            icon: <FiTrendingUp className="text-white text-2xl" />,
            color: "from-orange-500 to-orange-600"
        }
    ], []);

    const sections = {
        story: {
            title: "Our Story",
            content: (
                <div className="space-y-12">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                            className="space-y-6"
                        >
                            <h3 className="text-2xl font-bold text-gray-800">From Vision to Reality</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Founded in {companyData.founded}, we began as a small team of passionate technologists with a shared vision: to create digital solutions that make a real difference.
                            </p>
                            <p className="text-gray-600 leading-relaxed">
                                What started as late-night coding sessions and coffee-fueled brainstorming has grown into a thriving company serving clients across multiple industries.
                            </p>
                            <div className="space-y-4">
                                <div>
                                    <h4 className="font-bold text-gray-800">Our Mission</h4>
                                    <p className="text-gray-600">{companyData.mission}</p>
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800">Our Vision</h4>
                                    <p className="text-gray-600">{companyData.vision}</p>
                                </div>
                            </div>
                        </motion.div>
                        <div className="relative rounded-xl overflow-hidden aspect-video bg-gray-100 shadow-md">
                            <img
                                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80"
                                alt="Our team working together"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-6">
                                <p className="text-white">Our founding team in 2024</p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <StatCard stat={{
                            value: companyData.founded,
                            label: "Founded",
                            icon: <FiBriefcase className="text-blue-600" />
                        }} />
                        <StatCard stat={{
                            value: companyData.teamSize,
                            label: "Team Members",
                            icon: <FiUsers className="text-blue-600" />
                        }} />
                        <StatCard stat={{
                            value: companyData.clients,
                            label: "Happy Clients",
                            icon: <FiHeart className="text-blue-600" />
                        }} />
                        <StatCard stat={{
                            value: companyData.satisfaction,
                            label: "Satisfaction Rate",
                            icon: <FiCheck className="text-blue-600" />
                        }} />
                    </div>
                </div>
            )
        },
        values: {
            title: "Our Values",
            content: (
                <div className="space-y-16">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center max-w-3xl mx-auto"
                    >
                        <h2 className="text-3xl font-bold text-gray-800 mb-4">What Guides Us Every Day</h2>
                        <p className="text-gray-600 text-lg">
                            These principles shape our culture, inform our decisions, and define how we work with each other and our clients.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {values.map((value, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all border border-gray-100 group"
                            >
                                <div className={`bg-gradient-to-r ${value.color} p-8 relative overflow-hidden`}>
                                    <div className="absolute inset-0 bg-white/10 transform -skew-y-6 scale-150 opacity-20"></div>
                                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto relative z-10 group-hover:scale-110 transition-transform duration-500">
                                        {value.icon}
                                    </div>
                                </div>
                                <div className="p-6 text-center">
                                    <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                                    <p className="text-gray-600">{value.description}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            )
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Helmet>
                <title>About UniTechnoStack | Trusted Game & Web Development Company</title>
                <meta
                    name="description"
                    content="UniTechnoStack is a leading technology company delivering top-notch game development, web design, mobile apps, and custom software solutions worldwide."
                />
                <meta
                    name="keywords"
                    content="about UniTechnoStack, game development company, web development agency, mobile app developers, software solutions"
                />
                <meta name="robots" content="index, follow" />
                <link
                    rel="canonical"
                    href="https://www.unitechnostack.com/about"
                />

                {/* Open Graph tags */}
                <meta
                    property="og:title"
                    content="About UniTechnoStack | Trusted Game & Web Development Company"
                />
                <meta
                    property="og:description"
                    content="Discover UniTechnoStack's mission, vision, and expertise in game development, web design, mobile apps, and software solutions for global businesses."
                />
                <meta
                    property="og:url"
                    content="https://www.unitechnostack.com/about-us"
                />
                <meta property="og:type" content="website" />
            </Helmet>

            {/* Hero Section */}
            <section className="h-screen relative py-28 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white flex items-center justify-center">
                {/* Animated background elements */}
                <div className="absolute inset-0 overflow-hidden opacity-20">
                    {[...Array(20)].map((_, i) => (
                        <motion.div
                            key={i}
                            animate={{
                                y: [0, -100, 0],
                                x: [0, Math.random() * 80 - 40, 0],
                                opacity: [0.3, 1, 0.3],
                                scale: [0.8, 1.2, 0.8]
                            }}
                            transition={{
                                duration: 5 + Math.random() * 10,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                            className="absolute w-2 h-2 bg-white rounded-full"
                            style={{
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`
                            }}
                        />
                    ))}
                </div>

                {/* Grid pattern overlay */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
                        backgroundSize: '40px 40px'
                    }} />
                </div>

                <div className="relative max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-10"
                    >
                        <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
                            <span className="w-2 h-2 bg-blue-300 rounded-full mr-2 animate-pulse"></span>
                            About Our Company
                        </div>
                        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
                            Crafting <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-purple-300">Digital Excellence</span>
                        </h1>
                        <p className="text-xl sm:text-2xl max-w-3xl mx-auto text-blue-100">
                            We transform ideas into powerful digital experiences that drive business growth and innovation.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="flex justify-center gap-4 mt-10"
                    >
                        <a
                            href="/quickenquiry"
                            className="px-6 py-3 bg-white text-blue-600 rounded-lg font-bold hover:bg-gray-100 transition-colors shadow-lg flex items-center"
                        >
                            Start a Project <FiArrowRight className="ml-2" />
                        </a>
                        <a
                            href="#timeline"
                            className="px-6 py-3 border-2 border-white text-white rounded-lg font-bold hover:bg-white/10 transition-colors"
                        >
                            Explore Our Journey
                        </a>
                    </motion.div>
                </div>

                {/* Subtle scroll indicator */}
                <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
                    <FiChevronDown className="text-gray-400 w-8 h-8" />
                </div>

                {/* CSS for animations */}
                <style jsx>{`
    @keyframes float-slow {
      0%, 100% { transform: translate(0, 0) rotate(0deg); }
      50% { transform: translate(20px, 30px) rotate(5deg); }
    }
    @keyframes float-medium {
      0%, 100% { transform: translate(0, 0) rotate(0deg); }
      50% { transform: translate(-15px, -20px) rotate(-3deg); }
    }
    @keyframes gradient-shift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    .animate-float-slow { animation: float-slow 15s ease-in-out infinite; }
    .animate-float-medium { animation: float-medium 12s ease-in-out infinite; }
    .animate-gradient-shift { 
      background-size: 200% 200%;
      animation: gradient-shift 8s ease infinite; 
    } `}</style>
            </section>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                {/* Section Navigation */}
                <div className="flex justify-center mb-16">
                    <div className="inline-flex bg-white rounded-xl shadow-lg p-1 border border-gray-200">
                        {Object.entries(sections).map(([key, section]) => (
                            <button
                                key={key}
                                onClick={() => setActiveSection(key)}
                                className={`px-6 py-3 rounded-xl text-sm sm:text-base transition-all duration-300 flex items-center ${activeSection === key
                                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                                    : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                                    }`}
                            >
                                {activeSection === key && <div className="w-2 h-2 bg-white rounded-full mr-2"></div>}
                                {section.title}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content with smooth transitions */}

                <motion.div
                    key={activeSection}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className="mb-10"
                >
                    {sections[activeSection].content}
                </motion.div>


                {/* Timeline Section */}
                <section id="timeline" className="py-20">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-3xl font-bold text-gray-800 mb-3">Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Journey</span></h2>
                        <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                            Key milestones in our company's growth and development
                        </p>
                    </motion.div>

                    <div className="relative max-w-4xl mx-auto">
                        <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-purple-500 transform -translate-x-1/2" />

                        {timeline.map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.15 }}
                                viewport={{ once: true }}
                                className={`mb-12 flex ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'} items-start`}
                            >
                                <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                                    <div className="inline-flex items-center mb-4">
                                        <div className={`bg-gradient-to-r ${index % 2 === 0 ? 'from-blue-600 to-purple-600' : 'from-purple-600 to-blue-600'
                                            } w-14 h-14 rounded-full flex items-center justify-center mr-4 shadow-lg`}>
                                            {item.icon}
                                        </div>
                                        <span className="font-bold text-gray-800 text-lg bg-white py-1 px-3 rounded-full border border-gray-200 shadow-sm">{item.year}</span>
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-800 mb-2">{item.event}</h3>
                                    <p className="text-gray-600">{item.description}</p>
                                </div>

                                <div className="w-1/2 flex justify-center">
                                    <div className="w-6 h-6 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full border-4 border-white shadow-xl mt-4" />
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </section>

                <SecretInvestorsSection />

                {/* CTA Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl p-10 sm:p-14 text-center shadow-2xl relative overflow-hidden"
                >
                    {/* Background pattern */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
                            backgroundSize: '40px 40px'
                        }} />
                    </div>

                    <div className="relative z-10">
                        <h2 className="text-2xl sm:text-3xl font-bold mb-4">Ready to be part of our story?</h2>
                        <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
                            Whether you're looking for digital solutions or want to join our team, we'd love to hear from you.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                            <a
                                href="/quickenquiry"
                                className="bg-white text-blue-600 px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors shadow-sm flex items-center justify-center"
                            >
                                Contact Us <FiArrowRight className="ml-2" />
                            </a>
                            <a
                                href="/carrer"
                                className="border-2 border-white text-white px-6 py-3 rounded-lg font-bold hover:bg-white/10 transition-colors"
                            >
                                View Careers
                            </a>
                        </div>
                    </div>
                </motion.div>
            </main>

            {/* Video Modal */}
            <AnimatePresence>
                {isVideoModalOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
                        onClick={() => setIsVideoModalOpen(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white rounded-2xl overflow-hidden w-full max-w-4xl aspect-video"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="relative w-full h-full">
                                <button
                                    onClick={() => setIsVideoModalOpen(false)}
                                    className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                                >
                                    &times;
                                </button>
                                <div className="w-full h-full flex items-center justify-center bg-gray-900">
                                    <p className="text-white">Video content would appear here</p>
                                    {/* In a real implementation, you would embed a video player here */}
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AboutUsPage;